import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus, Trash2, ListChecks, Flag, GripVertical, Library, Loader2, Check, Lightbulb, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal editor for per-component deliverables. Bullets entered here flow
 * straight into the SOW (Section 3.3) and any bullet flagged
 * `is_milestone` is auto-promoted into the SOW Milestones table tied to
 * the component's phase end date.
 *
 * Props:
 *   componentName  string|null  — when truthy, modal is open for that component
 *   deliverables   Array<{id, text, is_milestone}>
 *   onClose        () => void
 *   onChange       (next) => void   — fired with the full updated array
 */
export function DeliverablesEditorDialog({ componentName, deliverables, onClose, onChange }) {
  const open = Boolean(componentName);
  const [items, setItems] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);

  // Reset local state every time the modal is opened for a new component
  useEffect(() => {
    if (open) {
      setItems(Array.isArray(deliverables) ? deliverables.map(d => ({ ...d })) : []);
      setLibraryOpen(false);
    }
  }, [componentName, open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch suggestion library every time the modal is opened for a component
  useEffect(() => {
    if (!open || !componentName) return undefined;
    let cancelled = false;
    setLoadingSuggestions(true);
    axios.get(`${API_URL}/api/deliverables/library`, {
      params: { component: componentName, limit: 15 },
      withCredentials: true,
    })
      .then(res => { if (!cancelled) setSuggestions(res.data?.suggestions || []); })
      .catch(() => { if (!cancelled) setSuggestions([]); })
      .finally(() => { if (!cancelled) setLoadingSuggestions(false); });
    return () => { cancelled = true; };
  }, [componentName, open]);

  const newId = () => (
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `d_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
  );

  const addItem = () => {
    setItems(prev => [...prev, { id: newId(), text: '', is_milestone: false, assumptions: [] }]);
  };

  const updateItem = (idx, patch) => {
    setItems(prev => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const addAssumption = (idx) => {
    setItems(prev => prev.map((it, i) => (
      i === idx ? { ...it, assumptions: [...(it.assumptions || []), ''] } : it
    )));
  };

  const updateAssumption = (idx, aIdx, value) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = [...(it.assumptions || [])];
      next[aIdx] = value;
      return { ...it, assumptions: next };
    }));
  };

  const removeAssumption = (idx, aIdx) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = (it.assumptions || []).filter((_, j) => j !== aIdx);
      return { ...it, assumptions: next };
    }));
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const moveItem = (idx, dir) => {
    setItems(prev => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const handleSave = () => {
    // Strip out fully-empty bullets so we don't pollute the SOW. Also
    // strip out empty per-deliverable assumption rows.
    const cleaned = items
      .map(it => ({
        ...it,
        text: (it.text || '').trim(),
        assumptions: (it.assumptions || [])
          .map(a => (a || '').trim())
          .filter(a => a.length > 0),
      }))
      .filter(it => it.text.length > 0);
    onChange(cleaned);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const milestoneCount = items.filter(it => it.is_milestone && (it.text || '').trim()).length;
  const filledCount = items.filter(it => (it.text || '').trim()).length;

  // Filter library to suggestions not already in the list (case-insensitive)
  const existingTexts = new Set(items.map(it => (it.text || '').trim().toLowerCase()).filter(Boolean));
  const availableSuggestions = suggestions.filter(s => !existingTexts.has((s.text || '').trim().toLowerCase()));

  const insertSuggestion = (sug) => {
    setItems(prev => [
      ...prev,
      {
        id: newId(),
        text: sug.text,
        // Pre-tick milestone toggle when ≥50% of historical uses agreed
        is_milestone: (sug.is_milestone_ratio || 0) >= 0.5,
        assumptions: [],
      },
    ]);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleCancel(); }}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="deliverables-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-cyan-600" />
            Deliverables — <span className="font-normal text-slate-600">{componentName}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Add the specific deliverables your team will produce for this
            component. These bullets appear in <strong>SOW Section 3.3</strong>.
            Flag a bullet as a <strong>milestone</strong> to auto-promote it into
            the SOW Milestones table at this component&apos;s phase end date.
            Per-deliverable <strong>assumptions</strong> flow into <strong>SOW
            Section 8.1 Component-Specific Assumptions</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 mt-2">
          {items.length === 0 && (
            <div className="text-xs text-slate-400 italic border border-dashed border-slate-200 rounded-md py-6 text-center">
              No deliverables yet — add the first one below.
            </div>
          )}

          {items.map((it, idx) => (
            <div
              key={it.id}
              className="flex items-start gap-2 bg-slate-50/60 border border-slate-200 rounded-md p-2"
              data-testid={`deliverable-row-${idx}`}
            >
              <div className="flex flex-col gap-0.5 pt-1.5 text-slate-400">
                <button
                  type="button"
                  onClick={() => moveItem(idx, -1)}
                  disabled={idx === 0}
                  className="hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-[10px] leading-none px-1"
                  title="Move up"
                  data-testid={`deliverable-move-up-${idx}`}
                >▲</button>
                <button
                  type="button"
                  onClick={() => moveItem(idx, 1)}
                  disabled={idx === items.length - 1}
                  className="hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-[10px] leading-none px-1"
                  title="Move down"
                  data-testid={`deliverable-move-down-${idx}`}
                >▼</button>
              </div>

              <GripVertical className="w-4 h-4 text-slate-300 mt-2" />

              <div className="flex-1 flex flex-col gap-1.5">
                <Input
                  value={it.text}
                  placeholder="e.g. Configure SAML SSO with client IdP and validate end-to-end login flow"
                  onChange={(e) => updateItem(idx, { text: e.target.value })}
                  className="h-8 text-sm"
                  data-testid={`deliverable-input-${idx}`}
                />
                <label
                  className={`inline-flex items-center gap-1.5 text-[11px] cursor-pointer select-none w-fit px-1.5 py-0.5 rounded border transition-colors ${
                    it.is_milestone
                      ? 'border-amber-300 bg-amber-50 text-amber-700'
                      : 'border-slate-200 text-slate-500 hover:border-amber-300 hover:text-amber-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={!!it.is_milestone}
                    onChange={(e) => updateItem(idx, { is_milestone: e.target.checked })}
                    className="w-3 h-3 accent-amber-500"
                    data-testid={`deliverable-milestone-toggle-${idx}`}
                  />
                  <Flag className="w-3 h-3" />
                  Promote to SOW Milestone
                </label>

                {/* Per-deliverable assumptions — flow into SOW Section 8.1
                    "Component-Specific Assumptions" subsection, grouped by
                    parent component. Use for scope caveats tied to this
                    specific deliverable (e.g. "max 25 learners per cohort"). */}
                <div className="mt-1.5 pl-2 border-l-2 border-cyan-100" data-testid={`deliverable-assumptions-block-${idx}`}>
                  <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-cyan-700/80 mb-1">
                    <Lightbulb className="w-3 h-3" />
                    Assumptions
                    <span className="text-slate-400 font-normal normal-case tracking-normal">
                      — flow into SOW §8.1
                    </span>
                  </div>
                  {(it.assumptions || []).map((a, aIdx) => (
                    <div key={aIdx} className="flex items-center gap-1.5 mb-1" data-testid={`deliverable-assumption-row-${idx}-${aIdx}`}>
                      <span className="text-slate-400 text-[10px]">•</span>
                      <Input
                        value={a}
                        placeholder="e.g. Max 25 learners per cohort"
                        onChange={(e) => updateAssumption(idx, aIdx, e.target.value)}
                        className="h-7 text-xs flex-1"
                        data-testid={`deliverable-assumption-input-${idx}-${aIdx}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeAssumption(idx, aIdx)}
                        className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Remove assumption"
                        data-testid={`deliverable-assumption-delete-${idx}-${aIdx}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addAssumption(idx)}
                    className="inline-flex items-center gap-1 text-[10px] text-cyan-700 hover:text-cyan-900 hover:underline mt-0.5"
                    data-testid={`deliverable-assumption-add-${idx}`}
                  >
                    <Plus className="w-3 h-3" />
                    Add assumption
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={() => removeItem(idx)}
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Remove deliverable"
                data-testid={`deliverable-delete-${idx}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}

          <div className="flex items-center gap-2 mt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addItem}
              className="flex-1 text-xs border-dashed"
              data-testid="deliverable-add-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add deliverable
            </Button>
            <Popover open={libraryOpen} onOpenChange={setLibraryOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs border-dashed"
                  data-testid="deliverable-library-btn"
                  title="Insert a phrase used historically on similar estimates"
                >
                  <Library className="w-3.5 h-3.5 mr-1" />
                  From library{availableSuggestions.length > 0 ? ` (${availableSuggestions.length})` : ''}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-96 max-h-80 overflow-y-auto p-0"
                align="end"
                data-testid="deliverable-library-popover"
              >
                <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/60">
                  <div className="text-[11px] font-semibold text-[#0F2040] flex items-center gap-1.5">
                    <Library className="w-3 h-3 text-cyan-600" />
                    Suggestions for &ldquo;{componentName}&rdquo;
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Pulled from past estimates you can see. Click to insert.
                  </div>
                </div>
                {loadingSuggestions && (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-4 h-4 animate-spin text-[#0F2040]" />
                  </div>
                )}
                {!loadingSuggestions && availableSuggestions.length === 0 && (
                  <div className="text-[11px] text-slate-400 italic py-6 text-center px-3">
                    {suggestions.length === 0
                      ? 'No deliverables have been authored for this component yet on prior estimates.'
                      : 'All historical suggestions are already in your list.'}
                  </div>
                )}
                {!loadingSuggestions && availableSuggestions.length > 0 && (
                  <ul className="divide-y divide-slate-100">
                    {availableSuggestions.map((s, i) => {
                      const willPromote = (s.is_milestone_ratio || 0) >= 0.5;
                      return (
                        <li key={`${s.text}-${i}`}>
                          <button
                            type="button"
                            onClick={() => { insertSuggestion(s); }}
                            className="w-full text-left px-3 py-2 hover:bg-cyan-50 transition-colors group flex items-start gap-2"
                            data-testid={`deliverable-library-item-${i}`}
                          >
                            <Plus className="w-3.5 h-3.5 text-slate-400 group-hover:text-cyan-600 mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-[#0F2040] leading-snug">{s.text}</div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[10px] text-slate-400">
                                  Used {s.frequency}×
                                </span>
                                {willPromote && (
                                  <span className="text-[10px] text-amber-700 inline-flex items-center gap-0.5">
                                    <Flag className="w-2.5 h-2.5" />
                                    usually a milestone
                                  </span>
                                )}
                              </div>
                            </div>
                            <Check className="w-3.5 h-3.5 text-transparent group-hover:text-cyan-600 mt-0.5 shrink-0" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <DialogFooter className="mt-3 flex-row items-center justify-between sm:justify-between">
          <Label className="text-[11px] text-slate-500 font-normal">
            {filledCount} bullet{filledCount === 1 ? '' : 's'}
            {milestoneCount > 0 && (
              <> · <span className="text-amber-700 font-medium">{milestoneCount} milestone{milestoneCount === 1 ? '' : 's'}</span></>
            )}
          </Label>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              data-testid="deliverable-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              className="bg-[#0F2040] hover:bg-[#0F2040]/90"
              data-testid="deliverable-save-btn"
            >
              Save deliverables
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DeliverablesEditorDialog;
